import DashboardNavigationLocations from "core/navigation/DashboardNavigationLocations";
import { StringsKeys } from "core/internationalization/strings";
import Colors from "core/colors/Colors";
import PublicNavigationLocations from "@/core/navigation/PublicNavigationLocations";
import type { FeaturePermissionKey } from "@/app/shared/permissions/featurePermissions";

export type MenuSectionTitle = {
  /** When set, user must satisfy canAny(featurePermissions) to see the section. */
  featurePermissions?: readonly FeaturePermissionKey[];
  sectionTitle: string;
};

export type MenuOptionsGroup = {
  featurePermissions?: readonly FeaturePermissionKey[];
  /** When set, user must also hold one of these raw role names (see hasAnyRoleLabel). */
  roleLabels?: readonly string[];
  icon?: string;
  title: StringsKeys;
  badgeContent?: string;
  badgeColor?: Colors;
  children: (MenuOptionsGroup | MenuOption)[];
};

export type MenuOption = {
  featurePermissions?: readonly FeaturePermissionKey[];
  /** When set, user must also hold one of these raw role names (see hasAnyRoleLabel). */
  roleLabels?: readonly string[];
  disabled?: boolean;
  icon?: string;
  location: PublicNavigationLocations | DashboardNavigationLocations;
  title: StringsKeys;
  badgeContent?: string;
  badgeColor?: Colors;
  externalLink?: boolean;
  openInNewTab?: boolean;
};

export type MenuItem = MenuOption | MenuOptionsGroup | MenuSectionTitle;

export type Menu = MenuItem[];

const menu: Menu = [
  {
    location: DashboardNavigationLocations.DASHBOARD,
    title: StringsKeys.dashboard,
  },
  {
    title: StringsKeys.dashboardsSection,
    featurePermissions: [
      "DASHBOARD_P1_READ",
      "DASHBOARD_P2_READ",
      "DASHBOARD_P3_READ",
      "DASHBOARD_P4_READ",
    ],
    children: [
      {
        location: DashboardNavigationLocations.COMMERCIAL_DASHBOARD,
        title: StringsKeys.dashboardCommercial,
        featurePermissions: ["DASHBOARD_P1_READ"],
      },
      {
        location: DashboardNavigationLocations.AGENCE_DASHBOARD,
        title: StringsKeys.dashboardAgence,
        featurePermissions: ["DASHBOARD_P2_READ"],
      },
      {
        location: DashboardNavigationLocations.COMPLIANCE_DASHBOARD,
        title: StringsKeys.dashboardCompliance,
        featurePermissions: ["DASHBOARD_P3_READ"],
      },
      {
        location: DashboardNavigationLocations.CONFORMITE_DASHBOARD,
        title: StringsKeys.dashboardConformite,
        featurePermissions: ["DASHBOARD_P4_READ"],
      },
    ],
  },
  // {
  //   location: DashboardNavigationLocations.CREATE_ACCOUNT,
  //   title: StringsKeys.createAccount,
  // },
  {
    title: StringsKeys.customers,
    children: [
      {
        location: DashboardNavigationLocations.PROSPECTS,
        title: StringsKeys.prospects,
        featurePermissions: ["CUSTOMERS_LIST_READ"],
      },
      {
        location: DashboardNavigationLocations.CLIENT_360,
        title: StringsKeys.client360,
        featurePermissions: ["CUSTOMERS_LIST_READ"],
      },
    ],
  },
  {
    title: StringsKeys.demandes,
    children: [
      {
        location: DashboardNavigationLocations.NOUVELLE_DEMANDE,
        title: StringsKeys.nouvelleDemande,
        featurePermissions: ["ONBOARDING_CASE_READ"],
      },
    ],
  },
  {
    title: StringsKeys.risquesConformite,
    featurePermissions: [
      "COMPLIANCE_L1_REVIEW_READ",
      "COMPLIANCE_P4_VALIDATION_READ",
      "COMPLIANCE_P4_VALIDATION_DECIDE",
      "COMPLIANCE_EDD_READ",
    ],
    children: [
      {
        location: DashboardNavigationLocations.COMPLIANCE_L1_REVIEW,
        title: StringsKeys.fileDeRevueL1,
        featurePermissions: ["COMPLIANCE_L1_REVIEW_READ"],
      },
      {
        location: DashboardNavigationLocations.COMPLIANCE_P4_VALIDATION,
        title: StringsKeys.validation4Yeux,
        featurePermissions: [
          "COMPLIANCE_P4_VALIDATION_READ",
          "COMPLIANCE_P4_VALIDATION_DECIDE",
        ],
      },
      {
        location: DashboardNavigationLocations.COMPLIANCE_EDD,
        title: StringsKeys.eddTitle,
        featurePermissions: ["COMPLIANCE_EDD_READ"],
      },
    ],
  },
  // Ce qui ne concerne que les entreprises (dossiers KYB, accès corporate) est séparé des files communes à tous les clients.
  {
    title: StringsKeys.kybMenuGroup,
    featurePermissions: [
      "COMPLIANCE_KYB_MONITORING_READ",
      "COMPLIANCE_KYB_ACCESS_MONITORING_READ",
      "COMPLIANCE_KYB_ACCESS_RECERTIFICATION_READ",
      "COMPLIANCE_KYB_RETENTION_READ",
      "COMPLIANCE_KYB_DOCUMENT_MATRIX_WRITE",
      "COMPLIANCE_KYB_COUNTRY_SCOPE_READ",
    ],
    children: [
      {
        location: DashboardNavigationLocations.KYB_MONITORING,
        title: StringsKeys.kybMonitoringMenu,
        featurePermissions: ["COMPLIANCE_KYB_MONITORING_READ", "COMPLIANCE_KYB_ACCESS_MONITORING_READ"],
      },
      {
        location: DashboardNavigationLocations.KYB_ACCESS_RECERTIFICATION,
        title: StringsKeys.kybRecertMenu,
        featurePermissions: ["COMPLIANCE_KYB_ACCESS_RECERTIFICATION_READ"],
      },
      {
        location: DashboardNavigationLocations.KYB_RETENTION,
        title: StringsKeys.kybRetentionMenu,
        featurePermissions: ["COMPLIANCE_KYB_RETENTION_READ"],
      },
      {
        location: DashboardNavigationLocations.KYB_PERIMETER,
        title: StringsKeys.kybPerimeterMenu,
        featurePermissions: ["COMPLIANCE_KYB_COUNTRY_SCOPE_READ"],
      },
      {
        location: DashboardNavigationLocations.KYB_COMPLEMENT_DOCUMENTS,
        title: StringsKeys.kybComplementTypesMenu,
        featurePermissions: ["COMPLIANCE_KYB_DOCUMENT_MATRIX_WRITE"],
      },
    ],
  },
];

export default menu;
